function normalizeObsUrl(endpoint = "") {
    const trimmed = endpoint.trim();
    if (!trimmed) {
        throw new Error("OBS WebSocket address is required.");
    }

    if (trimmed.startsWith("ws://") || trimmed.startsWith("wss://")) {
        return trimmed;
    }

    return `ws://${trimmed}`;
}

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });

    return btoa(binary);
}

async function sha256Base64(value) {
    const data = new TextEncoder().encode(value);
    return arrayBufferToBase64(await crypto.subtle.digest("SHA-256", data));
}

async function buildAuthentication(password, salt, challenge) {
    const secret = await sha256Base64(`${password}${salt}`);
    return sha256Base64(`${secret}${challenge}`);
}

export function createObsAdapter(config) {
    let socket;
    let requestId = 0;
    let connected = false;
    let pendingRequests = new Map();

    function sendMessage(op, data) {
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            throw new Error("OBS WebSocket is not connected.");
        }

        socket.send(JSON.stringify({ op, d: data }));
    }

    function cleanupPending(err) {
        pendingRequests.forEach(({ reject }) => reject(err));
        pendingRequests = new Map();
    }

    function request(requestType, requestData = {}) {
        const currentRequestId = String(++requestId);
        sendMessage(6, {
            requestType,
            requestId: currentRequestId,
            requestData,
        });

        return new Promise((resolve, reject) => {
            pendingRequests.set(currentRequestId, { resolve, reject });
        });
    }

    async function identify(helloData) {
        const identifyData = {
            rpcVersion: Math.min(helloData.rpcVersion || 1, 1),
        };

        if (helloData.authentication) {
            identifyData.authentication = await buildAuthentication(
                config.auth || "",
                helloData.authentication.salt,
                helloData.authentication.challenge,
            );
        }

        sendMessage(1, identifyData);
    }

    return {
        connect() {
            if (connected && socket?.readyState === WebSocket.OPEN) {
                return Promise.resolve(true);
            }

            return new Promise((resolve, reject) => {
                socket = new WebSocket(normalizeObsUrl(config.endpoint));

                socket.onmessage = async event => {
                    try {
                        const message = JSON.parse(event.data);

                        if (message.op === 0) {
                            await identify(message.d || {});
                        } else if (message.op === 2) {
                            connected = true;
                            resolve(true);
                        } else if (message.op === 7) {
                            const response = message.d || {};
                            const pending = pendingRequests.get(response.requestId);
                            if (pending) {
                                pendingRequests.delete(response.requestId);
                                if (response.requestStatus?.result) {
                                    pending.resolve(response);
                                } else {
                                    pending.reject(new Error(response.requestStatus?.comment || "OBS request failed."));
                                }
                            }
                        }
                    } catch (err) {
                        reject(err);
                    }
                };

                socket.onerror = () => {
                    const err = new Error("Unable to connect to OBS WebSocket.");
                    cleanupPending(err);
                    reject(err);
                };

                socket.onclose = () => {
                    connected = false;
                    cleanupPending(new Error("OBS WebSocket disconnected."));
                };
            });
        },
        async test() {
            await this.connect();
            return true;
        },
        async send(text = "") {
            await this.connect();
            return request("SendStreamCaption", { captionText: text });
        },
        async clear() {
            return this.send("");
        },
        async disconnect() {
            connected = false;
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
            socket = null;
            cleanupPending(new Error("OBS WebSocket disconnected."));
            return true;
        },
    };
}
